// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT license.

// <UserAuthConfigSnippet>
//import 'isomorphic-fetch';
import { DeviceCodeCredential, DeviceCodeCredentialOptions, DeviceCodePromptCallback, InteractiveBrowserCredential } from '@azure/identity';
import { Client, PageCollection } from '@microsoft/microsoft-graph-client';
import { User, Message } from '@microsoft/microsoft-graph-types';
import { TokenCredentialAuthenticationProvider } from
  '@microsoft/microsoft-graph-client/authProviders/azureTokenCredentials';

import { AppSettings } from './appSettings';

let _settings: AppSettings | undefined = undefined;
let _deviceCodeCredential: InteractiveBrowserCredential /*| undefined = undefined*/;
let _userClient: Client | undefined = undefined;

const errorMessage = 'Graph has not been initialized for user auth';

export function initializeGraphForUserAuth(settings: AppSettings, deviceCodePrompt: DeviceCodePromptCallback) {
  // Ensure settings isn't null
  if (!settings) {
    throw new Error('Settings cannot be undefined');
  }

  _settings = settings;

  try{
    _deviceCodeCredential = new InteractiveBrowserCredential({
      clientId: settings.clientId,
      tenantId: settings.tenantId,
    });
  } catch (err) {
      console.log(`Error getting user: ${err}`);
  }
  
  const authProvider = new TokenCredentialAuthenticationProvider(_deviceCodeCredential, {
    scopes: settings.graphUserScopes
  });

  _userClient = Client.initWithMiddleware({
    authProvider: authProvider
  });
}
// </UserAuthConfigSnippet>

// <GetUserTokenSnippet>
export async function getUserTokenAsync(): Promise<string> {
  // Ensure credential isn't undefined
  if (!_deviceCodeCredential) {
    throw new Error(errorMessage);
  }

  // Ensure scopes isn't undefined
  if (!_settings?.graphUserScopes) {
    throw new Error('Setting "scopes" cannot be undefined');
  }

  // Request token with given scopes
  const response = await _deviceCodeCredential.getToken(_settings?.graphUserScopes);
  return response.token;
}
// </GetUserTokenSnippet>

// <GetUserSnippet>
export async function getUserAsync(): Promise<User> {
  // Ensure client isn't undefined
  if (!_userClient) {
    throw new Error(errorMessage);
  }

  return _userClient.api('/me')
    // Only request specific properties
    //.select(['displayName', 'mail', 'userPrincipalName'])
    .get();
}
// </GetUserSnippet>

// <GetUserSnippet>
export async function getUsers(): Promise<any> {
  // Ensure client isn't undefined
  if (!_userClient) {
    throw new Error(errorMessage);
  }

  return _userClient.api('/users')
    // Only request specific properties
    //.select(['displayName', 'mail', 'userPrincipalName'])
    .get();
}
// </GetUserSnippet>

// <GetUserSnippet>
export async function getUsersWithFilter(namePart:string): Promise<any> {
  // Ensure client isn't undefined
  if (!_userClient) {
    throw new Error(errorMessage);
  }

  const usersByDisplayName = await _userClient.api('/users')
    .header('ConsistencyLevel', 'eventual')
    .search(`"displayName:${namePart}" OR "mail:${namePart}"`)
    .select(['id', 'displayName', 'mail', 'userPrincipalName', 'onPremisesExtensionAttributes'])
    .get();

  const usersByEmployeeId = await _userClient.api('/users')
    .header('ConsistencyLevel', 'eventual')
    .count(true)
    .filter(`startswith(onPremisesExtensionAttributes/extensionAttribute5,'${namePart}')`)
    .select(['id', 'displayName', 'mail', 'userPrincipalName', 'onPremisesExtensionAttributes'])
    .get();

  // Merge and ensure uniqueness
  const uniqueUsers = new Map();

  // Add users by display name
  for (const user of usersByDisplayName.value) {
      uniqueUsers.set(user.id, user);
  }

  // Add users by email, avoiding duplicates
  for (const user of usersByEmployeeId.value) {
      if (!uniqueUsers.has(user.id)) {
          uniqueUsers.set(user.id, user);
      }
  }

  //Convert map values back to array
  const combinedUsers = [...uniqueUsers.values()];

  return combinedUsers;
}
// </GetUserSnippet>

// <GetUserSnippet>
export async function getCoWorkers(): Promise<any> {
  // Ensure client isn't undefined
  if (!_userClient) {
    throw new Error(errorMessage);
  }

  return _userClient.api('/me/people')
    // Only request specific properties
    //.select(['displayName', 'mail', 'userPrincipalName'])
    .get();
}
// </GetUserSnippet>

// <GetUserSnippet>
export async function getUserEmail(email:string): Promise<User> {
  // Ensure client isn't undefined
  if (!_userClient) {
    throw new Error(errorMessage);
  }
  console.log('in get user by mail')
  return _userClient.api('/users/'+email)
    // Only request specific properties
    .select(['displayName', 'mail', 'userPrincipalName'])
    .get();
}
// </GetUserSnippet>

// <GetInboxSnippet>
export async function getInboxAsync(): Promise<PageCollection> {
  // Ensure client isn't undefined
  if (!_userClient) {
    throw new Error(errorMessage);
  }

  return _userClient.api('/me/mailFolders/inbox/messages')
    .select(['from', 'isRead', 'receivedDateTime', 'subject'])
    .top(25)
    .orderby('receivedDateTime DESC')
    .get();
}
// </GetInboxSnippet>

// <SendMailSnippet>
export async function sendMailAsync(subject: string, body: string, recipient: string) {
  // Ensure client isn't undefined
  if (!_userClient) {
    throw new Error(errorMessage);
  }

  // Create a new message
  const message: Message = {
    subject: subject,
    body: {
      content: body,
      contentType: 'text'
    },
    toRecipients: [
      {
        emailAddress: {
          address: recipient
        }
      }
    ]
  };

  // Send the message
  return _userClient.api('me/sendMail')
    .post({
      message: message
    });
}
// </SendMailSnippet>

// <MakeGraphCallSnippet>
// This function serves as a playground for testing Graph snippets
// or other code
export async function makeGraphCallAsync() {
  // INSERT YOUR CODE HERE
}
// </MakeGraphCallSnippet>
